<template>
  <BaseTemplate app-name="Grupos" page-title="">
    <template #content>
      <div class="container mx-auto p-6">
        <button
          @click="voltar"
          class="hidden md:inline-flex mb-5 items-center gap-2 bg-blue-500 dark:bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors dark:hover:bg-blue-700"
        >
          <SquareArrowLeft class="w-5 h-5" />
          <span>Voltar</span>
        </button>
        <div class="flex justify-between items-center mb-6">
          <h1 class="flex items-center gap-2 text-2xl font-bold text-gray-900 dark:text-white">
            <UsersRound class="w-6 h-6 text-blue-500" />
            Grupos
          </h1>
          <button
            @click="openModal()"
            class="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg shadow"
          >
            <PlusIcon class="w-5 h-5" />
            Novo Grupo
          </button>
        </div>

        <div class="flex flex-col sm:flex-row gap-4 mb-6">
          <input
            v-model="filter"
            type="text"
            placeholder="Buscar grupo..."
            class="w-full sm:w-1/2 border rounded-lg px-4 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <p v-if="error" class="text-red-500 text-center mb-4">{{ error }}</p>

        <div class="hidden sm:block bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto dark:text-white text-gray-800">
          <table class="w-full">
            <thead class="bg-blue-500 dark:bg-blue-600 text-white dark:text-gray-300 text-sm uppercase tracking-wide rounded-t-lg">
              <tr>
                <th class="px-4 py-3 text-left">Grupo</th>
                <th class="px-4 py-3 text-center">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="grupo in gruposFiltrados"
                :key="grupo.id"
                class="border-b dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <td class="px-4 py-3">{{ grupo.name }}</td>
                <td class="px-4 py-3 text-center">
                  <div class="flex justify-center space-x-2">
                    <button @click="openModal(grupo)" class="flex items-center gap-1 text-blue-500 hover:text-blue-700">
                      <Edit2Icon class="w-4 h-4" /> Editar
                    </button>
                    <button @click="deleteGroup(grupo)" class="flex items-center gap-1 text-red-500 hover:text-red-700">
                      <Trash2Icon class="w-4 h-4" /> Excluir
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="sm:hidden space-y-6">
          <div
            v-for="grupo in gruposFiltrados"
            :key="grupo.id"
            class="bg-white dark:bg-gray-900 shadow-lg rounded-xl p-6 border border-gray-100 dark:border-gray-700 transition transform hover:scale-105"
          >
            <div class="flex justify-between items-center">
              <h3 class="text-xl font-bold text-gray-900 dark:text-white">
                {{ grupo.name }}
              </h3>
              <div class="flex items-center gap-2">
                <button @click="openModal(grupo)" class="text-blue-500 hover:text-blue-700">
                  <Edit2Icon class="w-5 h-5" />
                </button>
                <button @click="deleteGroup(grupo)" class="text-red-500 hover:text-red-700">
                  <Trash2Icon class="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="isModalOpen"
          class="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
          @click.self="closeModal"
        >
          <div class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg w-96 animate-fade-in">
            <h2 class="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white mb-4">
              <UsersRound class="w-5 h-5 text-blue-500" />
              {{ isEditMode ? 'Editar Grupo' : 'Novo Grupo' }}
            </h2>

            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nome do Grupo</label>
            <input
              v-model="currentGroup.name"
              type="text"
              class="w-full border rounded-lg px-4 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white mb-3"
            />

            <div class="flex justify-end space-x-3">
              <button
                @click="closeModal"
                class="flex items-center gap-1 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg"
              >
                <XIcon class="w-4 h-4" /> Cancelar
              </button>
              <button
                @click="saveGroup"
                class="flex items-center gap-1 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg"
              >
                <SaveIcon class="w-4 h-4" />
                <span>Salvar</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>
  </BaseTemplate>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { UsersRound, Edit2Icon, Trash2Icon, SaveIcon, XIcon, SquareArrowLeft, PlusIcon } from 'lucide-vue-next'
import BaseTemplate from '../templates/BaseTemplate.vue'
import apiClient from '@/services/authService'
import { useRouter } from 'vue-router'

const router = useRouter()
const voltar = () => router.go(-1)

const grupos = ref([])
const filter = ref('')
const error = ref(null)
const isModalOpen = ref(false)
const isEditMode = ref(false)
const currentGroup = ref({ id: null, name: '' })

const gruposFiltrados = computed(() => {
  const termo = filter.value.trim().toLowerCase()
  if (!termo) return grupos.value
  return grupos.value.filter(grupo => (grupo.name || '').toLowerCase().includes(termo))
})

onMounted(() => {
  fetchGroups()
})

async function fetchGroups() {
  try {
    const response = await apiClient.get('/api/v1/auth/groups/')
    grupos.value = Array.isArray(response.data) ? response.data : []
  } catch (err) {
    error.value = 'Erro ao carregar grupos'
  }
}

function openModal(grupo = null) {
  if (grupo) {
    isEditMode.value = true
    currentGroup.value = { id: grupo.id, name: grupo.name }
  } else {
    isEditMode.value = false
    currentGroup.value = { id: null, name: '' }
  }
  error.value = null
  isModalOpen.value = true
}

function closeModal() {
  isModalOpen.value = false
  currentGroup.value = { id: null, name: '' }
}

async function saveGroup() {
  const nome = (currentGroup.value.name || '').trim()
  if (!nome) {
    error.value = 'Informe o nome do grupo'
    return
  }
  try {
    if (isEditMode.value && currentGroup.value.id) {
      await apiClient.put(`/api/v1/auth/groups/${currentGroup.value.id}/`, { name: nome })
    } else {
      await apiClient.post('/api/v1/auth/groups/', { name: nome })
    }
    await fetchGroups()
    closeModal()
  } catch (err) {
    error.value = 'Erro ao salvar grupo'
  }
}

async function deleteGroup(grupo) {
  if (!grupo?.id) return
  const confirmado = window.confirm(`Excluir o grupo "${grupo.name}"?`)
  if (!confirmado) return
  try {
    await apiClient.delete(`/api/v1/auth/groups/${grupo.id}/`)
    await fetchGroups()
  } catch (err) {
    error.value = 'Erro ao excluir grupo'
  }
}
</script>
